import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuthSession } from '../../features/authentication/presentation/hooks/useAuth.js';
import { useOrgList } from '../../features/org/presentation/hooks/useOrgList.js';
import type { OrgEntity } from '../../features/org/domain/entities/OrgEntity.js';
import type { AuthSessionEntity } from '../../features/authentication/domain/entities/AuthSessionEntity.js';

const LS_KEY = 'amax_active_org_id';

interface AppState {
  session: AuthSessionEntity | null;
  activeOrg: OrgEntity | null;
  orgs: OrgEntity[];
  orgsLoading: boolean;
  setActiveOrg: (org: OrgEntity) => void;
}

const AppStateContext = createContext<AppState>({
  session: null,
  activeOrg: null,
  orgs: [],
  orgsLoading: false,
  setActiveOrg: () => {},
});

export function AppStateProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useAuthSession();
  const { data: orgs = [], isLoading: orgsLoading } = useOrgList();
  const [activeOrg, setActiveOrgState] = useState<OrgEntity | null>(null);

  // Reset activeOrg on logout
  useEffect(() => {
    if (!session) setActiveOrgState(null);
  }, [session]);

  // Auto-select: restore from localStorage → fall back to first org
  useEffect(() => {
    if (!orgs.length) return;
    const savedId = localStorage.getItem(LS_KEY);
    const found = savedId ? (orgs.find((o) => o.id === savedId) ?? orgs[0]) : orgs[0];
    setActiveOrgState(found ?? null);
  }, [orgs]);

  const setActiveOrg = useCallback((org: OrgEntity) => {
    localStorage.setItem(LS_KEY, org.id ?? '');
    setActiveOrgState(org);
  }, []);

  const value = React.useMemo(() => ({
    session: session ?? null,
    activeOrg,
    orgs,
    orgsLoading,
    setActiveOrg,
  }), [session, activeOrg, orgs, orgsLoading, setActiveOrg]);

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
}

export function useAppState(): AppState {
  return useContext(AppStateContext);
}
